import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, isSupported, onMessage } from 'firebase/messaging';
import { registerWebPushDeviceToken, unregisterWebPushDeviceToken } from '../user/webPush.services';
import { resolveNotificationTarget } from '../../utils/notificationDeepLink';

const TOKEN_STORAGE_KEY = 'web_push_fcm_token';
const PERMISSION_PROMPT_STORAGE_KEY = 'web_push_permission_requested';
const FOREGROUND_HANDLER_FLAG = '__lmsWebPushForegroundHandlerAttached';
export const NOTIFICATION_REFRESH_EVENT = 'lms-notification-refresh';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || 'AIzaSyAI9DO_4URyEgS-O-oSlsM0aDrNA1I3_do',
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || 'lms-ellipsonic.firebaseapp.com',
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || 'lms-ellipsonic',
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || 'lms-ellipsonic.firebasestorage.app',
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '686240813252',
  appId: process.env.REACT_APP_FIREBASE_APP_ID || '1:686240813252:web:b04a8ef1d3ca93100a3991',
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || 'G-VMKGEPWQTE',
};

let firebaseAppInstance = null;
let messagingInstance = null;

function getStoredUser() {
  try {
    return JSON.parse(localStorage.getItem('user') || 'null');
  } catch (error) {
    return null;
  }
}

function getStoredUserId() {
  const user = getStoredUser();
  return user?.id || user?.userId || user?.user_id || null;
}

function getDeepLinkFromPayload(payload) {
  // Prefer resolving the in-app destination from the structured event + data so
  // foreground push clicks land on the exact page/tab the notification is about.
  const data = payload?.data || {};
  if (data.eventType) {
    try {
      const resolved = resolveNotificationTarget(
        { event: data.eventType, content_json: { data } },
        localStorage.getItem('role')
      );
      if (resolved) {
        return resolved;
      }
    } catch (error) {
      // fall through to legacy link fields
    }
  }

  return (
    payload?.fcmOptions?.link ||
    payload?.data?.deepLink ||
    payload?.data?.link ||
    payload?.notification?.click_action ||
    '/'
  );
}

function notifyInboxRefresh(payload) {
  window.dispatchEvent(
    new CustomEvent(NOTIFICATION_REFRESH_EVENT, {
      detail: payload,
    })
  );
}

function normalizeNotificationLink(link = '/') {
  if (!link) {
    return '/';
  }

  return link.startsWith('http')
    ? link
    : `${window.location.origin}${link.startsWith('/') ? '' : '/'}${link}`;
}

async function showNotificationWithRegistration(title, options = {}) {
  try {
    const registration = await navigator.serviceWorker.ready;
    if (registration?.showNotification) {
      await registration.showNotification(title, options);
      return true;
    }
  } catch (error) {
    console.warn('[WebPush] Failed to display service worker notification:', error);
  }

  return false;
}

export async function showBrowserSystemNotification({
  title = 'Notification',
  body = '',
  deepLink = '/',
  tag,
} = {}) {
  if (typeof window === 'undefined' || typeof Notification === 'undefined') {
    return false;
  }

  if (Notification.permission !== 'granted') {
    return false;
  }

  const normalizedLink = normalizeNotificationLink(deepLink);
  const notificationOptions = {
    body,
    tag: tag || normalizedLink,
    data: {
      link: normalizedLink,
    },
  };

  const displayedByServiceWorker = await showNotificationWithRegistration(title, notificationOptions);
  if (displayedByServiceWorker) {
    return true;
  }

  const notification = new Notification(title, { body, tag: notificationOptions.tag });
  notification.onclick = () => {
    window.open(normalizedLink, '_blank', 'noopener,noreferrer');
    notification.close();
  };

  return true;
}

async function showForegroundSystemNotification(payload) {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
    return;
  }

  const title = payload?.notification?.title || 'Notification';
  const body = payload?.notification?.body || '';
  const link = getDeepLinkFromPayload(payload);
  await showBrowserSystemNotification({
    title,
    body,
    deepLink: link,
    tag: payload?.data?.notificationId || payload?.messageId || link,
  });
}

async function ensureSupported() {
  if (typeof window === 'undefined' || typeof Notification === 'undefined' || !('serviceWorker' in navigator)) {
    return false;
  }

  return isSupported();
}

function getFirebaseApp() {
  if (!firebaseAppInstance) {
    firebaseAppInstance = initializeApp(firebaseConfig);
  }

  return firebaseAppInstance;
}

async function getMessagingInstance() {
  const supported = await ensureSupported();
  if (!supported) {
    return null;
  }

  if (!messagingInstance) {
    messagingInstance = getMessaging(getFirebaseApp());
  }

  return messagingInstance;
}

async function registerServiceWorker() {
  return navigator.serviceWorker.register('/firebase-messaging-sw.js');
}

async function fetchWebPushToken() {
  const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;

  const messaging = await getMessagingInstance();
  if (!messaging) {
    return null;
  }

  const registration = await registerServiceWorker();
  // Firebase Messaging uses the project's default public VAPID key when
  // vapidKey is omitted. A custom REACT_APP_FIREBASE_VAPID_KEY can still be
  // supplied for projects that use their own Web Push certificate.
  const tokenOptions = { serviceWorkerRegistration: registration };
  if (vapidKey) {
    tokenOptions.vapidKey = vapidKey;
  }

  return getToken(messaging, tokenOptions);
}

export function resetWebPushPromptState() {
  sessionStorage.removeItem(PERMISSION_PROMPT_STORAGE_KEY);
}

export async function ensureWebPushRegistration({ requestPermission = true } = {}) {
  const userId = getStoredUserId();
  if (!userId) {
    return { status: 'no_user' };
  }

  try {
    const supported = await ensureSupported();
    if (!supported) {
      return { status: 'unsupported' };
    }

    let permission = Notification.permission;

    if (permission === 'default' && requestPermission) {
      sessionStorage.setItem(PERMISSION_PROMPT_STORAGE_KEY, '1');
      permission = await Notification.requestPermission();
    }

    if (permission !== 'granted') {
      return { status: permission === 'denied' ? 'denied' : 'not_granted' };
    }

    const token = await fetchWebPushToken();
    if (!token) {
      return { status: 'no_token' };
    }

    await registerWebPushDeviceToken({
      platform: 'web',
      token,
    });

    localStorage.setItem(TOKEN_STORAGE_KEY, token);

    return { status: 'registered', token };
  } catch (error) {
    console.warn('[WebPush] Registration failed:', error);
    return { status: 'error', error };
  }
}

export async function revokeCurrentWebPushToken() {
  const token = localStorage.getItem(TOKEN_STORAGE_KEY);
  if (!token) {
    return { status: 'no_cached_token' };
  }

  try {
    await unregisterWebPushDeviceToken({
      platform: 'web',
      token,
    });
  } finally {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    resetWebPushPromptState();
  }

  return { status: 'revoked' };
}

export async function attachForegroundWebPushHandler() {
  if (window[FOREGROUND_HANDLER_FLAG]) {
    return;
  }

  const messaging = await getMessagingInstance();
  if (!messaging) {
    return;
  }

  onMessage(messaging, (payload) => {
    notifyInboxRefresh(payload);
    showForegroundSystemNotification(payload).catch((error) => {
      console.warn('[WebPush] Failed to display foreground system notification:', error);
    });
  });

  window[FOREGROUND_HANDLER_FLAG] = true;
}
